import React, {useEffect, useMemo, useState} from "react";
import CustomDialog from "../../components/general/CustomDialog";
import useInputValue from "../../hooks/useInputValue";
import CustomInput from "../../components/general/CustomInput";
import {useTranslation} from "react-i18next";
import getConstrains from "../../utils/validationConstraints";
import useValidation from "../../hooks/useValidation";
import CustomButton from "../../components/general/CustomButton";
import {showToast} from "../../components/general/CustomToast";
import {useCreateUser, UserChangeData, useUpdateUser} from "../../services/admin-user/apiUsers";
import CustomSelect from "../../components/general/CustomSelect";
import {UserInterface} from "../../services/interface/UserInterface";
import {UserKeycloakInterface} from "../../services/interface/UserKeycloakInterface";

interface Props {
    title: string;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    userDB?: UserInterface;
    userKeycloak?: UserKeycloakInterface;
}

export default function DialogCreateUser({title, open, setOpen, userDB, userKeycloak}: Props) {
    const {t} = useTranslation("global") as { t: (key: string) => string }
    const {mutate: createUser, isPending, data: successCreateUser} = useCreateUser()
    const {mutate: editUser, isPending: isLoadingEdit, data: successEditUser} = useUpdateUser()
    const user = {idSelect: 1, descriptionData: 'USUARIO'}

    const [errorValidation, setErrorValidation]: any = useState(null);
    const validationSchema = useMemo(
        () => getConstrains(['email', 'name', 'lastname', 'phone', 'username', 'password']),
        []
    );
    const validation = useValidation(validationSchema);

    const name = useInputValue('')
    const lastname = useInputValue('')
    const phone = useInputValue('')
    const email = useInputValue('')
    const username = useInputValue('')
    const password = useInputValue('')
    const password2 = useInputValue('')
    const [rol, setRol] = useState(user)

    useEffect(() => {
        if (successCreateUser || successEditUser) {
            showToast(title === t('create-user') ? 'Usuario creado.' : "Usuario editado.", 'success')
            name.onChangeText('')
            lastname.onChangeText('')
            phone.onChangeText('')
            email.onChangeText('')
            username.onChangeText('')
            password.onChangeText('')
            setRol(user)
            setOpen(false)
        }
    }, [successCreateUser, successEditUser]);

    useEffect(() => {
        if (title === t('edit-user') && userKeycloak && userDB) {
            name.onChangeText(userDB?.nombre ? userDB?.nombre : '')
            lastname.onChangeText(userDB?.apellido ? userDB?.apellido : '')
            phone.onChangeText(userDB?.celular ? userDB?.celular : '')
            email.onChangeText(userDB?.correo ? userDB?.correo : '')
            username.onChangeText(userDB?.username ? userDB?.username : '')
            password.onChangeText('default')
            setRol({idSelect: userDB.rol ? userDB.rol : 1, descriptionData: userDB.rolDescripcion ? userDB.rolDescripcion : 'USUARIO'})
        }
    }, [title, userKeycloak, userDB]);

    return (
        <>
            <CustomDialog
                size={'md'}
                body={
                    <div>
                        <div className={'grid grid-cols-2 gap-6'}>
                            <CustomInput
                                type={'letters'}
                                labelFixel={true}
                                label={t('name')}
                                error={errorValidation?.name}
                                {...name}
                            />
                            <CustomInput
                                type={'letters'}
                                labelFixel={true}
                                label={t('lastname')}
                                error={errorValidation?.lastname}
                                {...lastname}
                            />
                            <CustomInput
                                type={'telephone'}
                                labelFixel={true}
                                label={t('phone')}
                                error={errorValidation?.phone}
                                {...phone}
                            />
                            <CustomInput
                                type={'mail'}
                                labelFixel={true}
                                label={t('email')}
                                error={errorValidation?.email}
                                {...email}
                            />
                            <CustomInput
                                disabled={title === t('edit-user')}
                                type={'text'}
                                labelFixel={true}
                                label={t('username')}
                                error={errorValidation?.username}
                                {...username}
                            />
                            <CustomSelect
                                label={t('rol')}
                                value={rol}
                                setValue={setRol}
                                options={[
                                    {idSelect: 1, descriptionData: 'USUARIO'},
                                    {idSelect: 0, descriptionData: 'ADMINISTRADOR'},
                                ]}
                            />
                            {title === t('create-user') && <>
                                <CustomInput
                                    type={'password'}
                                    labelFixel={true}
                                    label={t('password')}
                                    error={errorValidation?.password}
                                    {...password}
                                />
                                <CustomInput
                                    type={'password'}
                                    labelFixel={true}
                                    label={t('repeat-password')}
                                    error={password.value !== password2.value ? t('valid-password') : ''}
                                    {...password2}
                                />
                            </>}
                        </div>
                        <div className={'flex justify-end mt-3'}>
                            <CustomButton
                                text={t('save')}
                                loading={isPending || isLoadingEdit}
                                disabled={isPending || isLoadingEdit || (password.value !== password2.value && title === t('create-user'))}
                                onClick={() => {
                                    const data: any = {
                                        username: username.value,
                                        nombre: name.value,
                                        name: name.value,
                                        firstName: name.value,
                                        lastname: lastname.value,
                                        apellido: lastname.value,
                                        lastName: lastname.value,
                                        email: email.value,
                                        correo: email.value,
                                        celular: phone.value,
                                        phone: phone.value,
                                        password: password.value,
                                        rol: rol.idSelect,
                                        enabled: true,
                                        credentials: [
                                            {
                                                temporary: false,
                                                value: password.value, // Contrasenia keycloak
                                                type: 'password',
                                            },
                                        ],
                                    }
                                    const errors = validation(data);
                                    if (errors !== null && Object.keys(errors).length) {
                                        setErrorValidation(errors)
                                        showToast(
                                            'Campos obligatorios por llenar (*)',
                                            'warning-info'
                                        );
                                    } else {
                                        setErrorValidation(null);
                                        if (title === t('create-user')) {
                                            createUser(data);
                                        } else {
                                            const userEdit: UserChangeData = {
                                                user: {
                                                    username: username.value,
                                                    nombre: name.value,
                                                    apellido: lastname.value,
                                                    correo: email.value,
                                                    celular: phone.value,
                                                    rol: rol.idSelect,
                                                },
                                                userKeycloak: {
                                                    ...userKeycloak,
                                                    email: email.value,
                                                    firstName: name.value,
                                                    lastName: lastname.value
                                                }
                                            }
                                            editUser(userEdit)
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                }
                {...{title, open, setOpen}}
            />
        </>
    )
}
